import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useRouter } from "@tanstack/react-router";
import { Button } from "./catalyst/Button";
import { Logo } from "./Logo";

interface GlobalErrorProps {
	error?: Error;
	reset?: () => void;
}

/**
 * Global error component for uncaught routing and rendering errors.
 * Shows a friendly error message with options to retry or go home.
 */
export function GlobalError({ error, reset }: GlobalErrorProps) {
	const router = useRouter();

	const handleRetry = () => {
		if (reset) {
			reset();
		} else {
			router.invalidate();
		}
	};

	const handleGoHome = () => {
		router.navigate({ to: "/" });
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-base-100">
			{/* Background pattern */}
			<div className="absolute inset-0 opacity-5">
				<div
					className="h-full w-full"
					style={{
						backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
						backgroundSize: "24px 24px",
					}}
				/>
			</div>

			<div className="relative z-10 flex max-w-lg flex-col items-center gap-8 px-6 text-center">
				{/* Logo */}
				<div className="text-base-content/30">
					<Logo />
				</div>

				{/* Error icon with animated ring */}
				<div className="relative">
					<div
						className="absolute -inset-4 animate-ping rounded-full bg-error/20"
						style={{ animationDuration: "2s" }}
					/>
					<div className="absolute -inset-4 rounded-full bg-error/10" />
					<div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-error/10 ring-2 ring-error/20">
						<ExclamationTriangleIcon className="h-10 w-10 text-error" />
					</div>
				</div>

				{/* Error message */}
				<div className="space-y-3">
					<h1 className="text-2xl font-bold text-base-content">
						Something went wrong
					</h1>
					<p className="text-base-content/60">
						We encountered an unexpected error. Don't worry, your data is safe.
					</p>
				</div>

				{/* Error details (collapsible in dev) */}
				{import.meta.env.DEV && error?.message && (
					<details className="w-full rounded-lg bg-base-200 p-4 text-left">
						<summary className="cursor-pointer text-sm font-medium text-base-content/70 hover:text-base-content">
							Error details
						</summary>
						<pre className="mt-3 overflow-x-auto whitespace-pre-wrap wrap-break-word text-xs text-error">
							{error.message}
							{error.stack && (
								<>
									{"\n\n"}
									{error.stack}
								</>
							)}
						</pre>
					</details>
				)}

				{/* Action buttons */}
				<div className="flex flex-wrap items-center justify-center gap-4">
					<Button onClick={handleRetry} color="blue">
						Try again
					</Button>
					<Button onClick={handleGoHome} outline>
						Go to dashboard
					</Button>
				</div>

				{/* Support link */}
				<p className="text-xs text-base-content/40">
					If this keeps happening, please{" "}
					<a
						href="mailto:support@biteasy.com"
						className="text-primary hover:underline"
					>
						contact support
					</a>
				</p>
			</div>
		</div>
	);
}

/**
 * Error component adapter for TanStack Router's error boundary.
 * TanStack Router passes error info in a specific format.
 */
export function RouterErrorComponent({ error }: { error: unknown }) {
	const errorObject =
		error instanceof Error
			? error
			: new Error(String(error) || "An unknown error occurred");

	return <GlobalError error={errorObject} />;
}
